import {assert} from 'Asserts';
import preferences from 'Preferences';
import {refreshStudyFolder} from 'studies/FileUtils';
import {
  ExperimentCreatePendingTask,
  getExperimentUnknownErrorAction,
  onOpenAfterExperimentCreate,
  onSuccessAfterExperimentCreate,
  showExperimentCreationTrayTask,
} from 'studies/sidepanel/ExperimentCreationTrayTask';
import {
  STUDY_UNKNOWN_ERROR_ACTION,
  StudyCreatePendingTask,
  onOpenStudyAfterCreate,
  showStudyCreationTrayTask,
} from 'studies/sidepanel/StudyCreationTrayTask';
import {
  ConditionCreatePendingTask,
  getConditionCreationUnknownErrorAction,
  onOpenAfterAddConditions,
  onSuccessAfterAddConditions,
  showConditionCreationTrayTask,
} from 'studies/typed/shared/ConditionCreationTrayTask';
import {backfillStudyTypeOnCreates} from 'studies/TypedStudyUtils';
import {warn} from 'util/Logging';
import * as net from 'util/Net';
import {assertNever} from 'util/Types';

// Keep in sync with benchling.studies.progress.persisted_task_status.TASK_PREFIX
// This is used to retrieve persisted task state and should not be changed
export const TASK_USER_PREFERENCE_KEY_PREFIX = 'task:';

export type PendingTask = StudyCreatePendingTask | ExperimentCreatePendingTask | ConditionCreatePendingTask;

function backfillTask(task: PendingTask): PendingTask {
  switch (task.type) {
    case 'STUDY':
      return task;
    case 'EXPERIMENT':
      return {
        ...task,
        requestMeta: {
          ...task.requestMeta,
          // Previous versions of the metadata did not include the studyType field
          // In those cases, we default to ASSAY_DEVELOPMENT
          creates: backfillStudyTypeOnCreates(task.requestMeta.creates),
        },
      };
    case 'CONDITION':
      return task;
    default:
      assertNever(task);
      return task;
  }
}

export function resumePersistedTasks(): void {
  for (const task of getAllTasks()) {
    try {
      switch (task.type) {
        case 'STUDY': {
          assert(task.requestMeta.creates.length === 1, 'Expected one study to be created');
          showStudyCreationTrayTask(
            task.taskId,
            task.statusURL,
            task.taskMeta,
            task.requestMeta.creates,
            {
              onSuccess: () => {
                refreshStudyFolder(task.requestMeta.creates[0].folderId);
              },
              onOpen: onOpenStudyAfterCreate,
              onUnknownError: STUDY_UNKNOWN_ERROR_ACTION,
            },
            task.timingMeta,
            task
          );
          break;
        }
        case 'EXPERIMENT': {
          assert(
            new Set(task.requestMeta.creates.map((create) => create.studyId)).size === 1,
            'Exected all creates to have the same studyId'
          );
          showExperimentCreationTrayTask(
            task.taskId,
            task.statusURL,
            task.taskMeta,
            task.requestMeta.creates,
            {
              onSuccess: (response) => {
                onSuccessAfterExperimentCreate(
                  task.requestMeta.creates[0].studyId,
                  response,
                  task.requestMeta.creates[0].studyType
                );
              },
              onOpen: (response) => {
                // we currently only support creating one procedure run per request
                onOpenAfterExperimentCreate(task.requestMeta.creates[0].studyId, response.creates[0].id);
              },
              onUnknownError: getExperimentUnknownErrorAction(
                task.requestMeta.creates[0].studyId,
                task.requestMeta.creates[0].studyType
              ),
            },
            task.timingMeta,
            task
          );
          break;
        }
        case 'CONDITION': {
          showConditionCreationTrayTask(
            task.taskId,
            task.statusURL,
            task.taskMeta,
            task.requestMeta.updates,
            {
              onSuccess: (response) => {
                onSuccessAfterAddConditions(task.requestMeta.updates[0].id);
              },
              onOpen: () => {
                onOpenAfterAddConditions(task.requestMeta.updates[0].studyId, task.requestMeta.updates[0].id);
              },
              onUnknownError: getConditionCreationUnknownErrorAction(
                task.requestMeta.updates[0].studyId,
                task.requestMeta.updates[0].studyType,
                task.requestMeta.updates[0].id,
                task.requestMeta.updates[0].procedureType,
                task.requestMeta.updates[0].numNewConditions
              ),
            },
            task.timingMeta,
            task
          );
          break;
        }
        default:
          assert(false, `Unexpected task type: ${assertNever(task)}`);
      }
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      warn(e);
    }
  }
}

export function getAllTasks(): Array<PendingTask> {
  return Object.values(
    preferences.pick((value, key) => key.startsWith(TASK_USER_PREFERENCE_KEY_PREFIX)) as Record<
      string,
      PendingTask
    >
  ).map((task) => backfillTask(task));
}

export async function fetchPersistedTaskStatus(taskId: string): Promise<PendingTask | undefined> {
  const prefs = await net.getJSONPromise('/1/api/users/me/preferences');
  const task = prefs[`${TASK_USER_PREFERENCE_KEY_PREFIX}${taskId}`];
  // only backfill task if it's defined
  return task ? backfillTask(task) : task;
}

export function clearTask(taskId: string): void {
  preferences.unset(`${TASK_USER_PREFERENCE_KEY_PREFIX}${taskId}`);
}
